'use client';

import type { MouseEvent } from 'react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  fetchConferenceChatAuthInfo,
  getConferenceChatAuthApiBySite,
  isConferenceChatGuestSelf,
  openConferenceChatEntry,
} from '../../navbar/conferenceChat';
import type { ConferenceChatAuthInfo } from '../../navbar/conferenceChat';
import Image from '../../image';
import type {
  ConferenceAgendaDetailResp,
  ConferenceAgendaForumItem,
  ConferenceAgendaGuestItem,
  ConferenceAgendaItem,
  ConferenceAgendaSessionItem,
  ConferenceSite,
} from '../conference/types';
import { formatConferenceAgendaClockRange } from '../conference/agenda.helpers';
import styles from './ConferenceChatConferenceAgendaSection.module.css';

interface ConferenceChatConferenceAgendaSectionProps {
  data?: ConferenceAgendaDetailResp;
  title: string;
  site?: ConferenceSite;
  loading?: boolean;
}

const getLocaleText = (isZh: boolean, zh?: string, en?: string) =>
  (isZh ? zh : en) || '';

const getForumList = (data?: ConferenceAgendaDetailResp) =>
  data?.ForumList ||
  data?.forumList ||
  data?.Forums ||
  data?.forums ||
  data?.list ||
  [];

const getSessionList = (forum?: ConferenceAgendaForumItem) =>
  forum?.SessionList ||
  forum?.sessionList ||
  forum?.Sessions ||
  forum?.sessions ||
  [];

const getAgendaList = (session?: ConferenceAgendaSessionItem) =>
  session?.AgendaList ||
  session?.agendaList ||
  session?.Agendas ||
  session?.agendas ||
  session?.Items ||
  session?.items ||
  [];

const getGuestList = (agenda?: ConferenceAgendaItem) =>
  agenda?.GuestList ||
  agenda?.guestList ||
  agenda?.Guests ||
  agenda?.guests ||
  [];

const getGuestID = (guest?: ConferenceAgendaGuestItem) =>
  guest?.GuestID || guest?.guestId || '';

const getGuestDataSource = (guest?: ConferenceAgendaGuestItem) =>
  guest?.GuestDataSource || guest?.guestDataSource || '';

const isGuestContactAllowed = (guest?: ConferenceAgendaGuestItem) =>
  (guest?.AllowContact ?? guest?.allowContact ?? 1) !== 0;

const getForumID = (forum?: ConferenceAgendaForumItem, index = 0) =>
  forum?.ForumID || forum?.forumId || `forum-${index}`;

const getSessionID = (session?: ConferenceAgendaSessionItem, index = 0) =>
  session?.SessionID || session?.sessionId || `session-${index}`;

const getAgendaID = (agenda?: ConferenceAgendaItem, index = 0) =>
  agenda?.AgendaID ||
  agenda?.AgendaItemID ||
  agenda?.agendaId ||
  agenda?.agendaItemId ||
  `agenda-${index}`;

const getGuestName = (guest: ConferenceAgendaGuestItem, isZh: boolean) =>
  getLocaleText(
    isZh,
    guest.NameCN || guest.nameCn,
    guest.NameEN || guest.nameEn
  );

const getGuestTitle = (guest: ConferenceAgendaGuestItem, isZh: boolean) => {
  const titles = guest.Titles || guest.titles || [];
  const titleText = titles
    .map((item) => getLocaleText(isZh, item.TitleCN, item.TitleEN))
    .filter(Boolean)
    .join(' · ');

  return (
    titleText ||
    guest.Position ||
    guest.position ||
    guest.Organization ||
    guest.organization ||
    ''
  );
};

const getAgendaTitle = (agenda: ConferenceAgendaItem, isZh: boolean) =>
  getLocaleText(
    isZh,
    agenda.TitleCN || agenda.titleCn,
    agenda.TitleEN || agenda.titleEn
  );

const FORUM_TABS_SCROLL_EDGE_TOLERANCE = 2;
const MOBILE_FORUM_TAB_EDGE_GAP = 16;

const clampScrollLeft = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const normalizeForumTabsScrollLeft = (value: number, maxScrollLeft: number) => {
  if (maxScrollLeft <= FORUM_TABS_SCROLL_EDGE_TOLERANCE) {
    return 0;
  }

  const nextValue = clampScrollLeft(value, 0, maxScrollLeft);
  if (nextValue <= FORUM_TABS_SCROLL_EDGE_TOLERANCE) {
    return 0;
  }
  if (maxScrollLeft - nextValue <= FORUM_TABS_SCROLL_EDGE_TOLERANCE) {
    return maxScrollLeft;
  }
  return nextValue;
};

const GuestActionIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle
      cx="12"
      cy="12"
      r="11.808"
      fill="var(--agenda-chat-icon-fill, #EBF1FF)"
      stroke="var(--agenda-chat-icon-border, #D7E1F7)"
      strokeWidth="0.384"
    />
    <path
      d="M5.75391 8.16703C5.75391 7.63684 6.18371 7.20703 6.71391 7.20703H16.7939C17.3241 7.20703 17.7539 7.63684 17.7539 8.16703V14.6763C17.7539 15.2065 17.3241 15.6363 16.7939 15.6363H7.99835C7.88988 15.6363 7.7846 15.673 7.69969 15.7405L5.75391 17.287V8.16703Z"
      fill="var(--agenda-chat-icon-message, #0040DD)"
      fillOpacity="var(--agenda-chat-icon-message-opacity, 0.7)"
    />
    <circle
      cx="8.87625"
      cy="11.2766"
      r="0.72"
      fill="var(--agenda-chat-icon-fill, #EBF1FF)"
    />
    <circle
      cx="11.7591"
      cy="11.2766"
      r="0.72"
      fill="var(--agenda-chat-icon-fill, #EBF1FF)"
    />
    <circle
      cx="14.638"
      cy="11.2766"
      r="0.72"
      fill="var(--agenda-chat-icon-fill, #EBF1FF)"
    />
  </svg>
);

const ConferenceAgendaEmptyStateIllustration = () => {
  const idSeed = useId().replace(/:/g, '');
  const mask0Id = `conference-agenda-empty-mask0-${idSeed}`;
  const mask1Id = `conference-agenda-empty-mask1-${idSeed}`;
  const filter0Id = `conference-agenda-empty-filter0-${idSeed}`;
  const filter1Id = `conference-agenda-empty-filter1-${idSeed}`;
  const filter2Id = `conference-agenda-empty-filter2-${idSeed}`;

  return (
    <svg
      className={styles.emptyIllustration}
      width="200"
      height="140"
      viewBox="0 0 200 140"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <mask
        id={mask0Id}
        style={{ maskType: 'alpha' }}
        maskUnits="userSpaceOnUse"
        x="30"
        y="62"
        width="88"
        height="60"
      >
        <path d="M30 62H118V122H30V62Z" fill="#97A3B7" />
      </mask>
      <g mask={`url(#${mask0Id})`}>
        <path d="M30 62H118V122H30V62Z" fill="#97A3B7" />
        <g filter={`url(#${filter0Id})`}>
          <rect x="12" y="84" width="80" height="60" fill="#E3E6EB" />
        </g>
        <g filter={`url(#${filter1Id})`}>
          <rect x="80" y="54" width="80" height="60" fill="#E3E6EB" />
        </g>
        <rect
          width="21"
          height="2"
          transform="matrix(1 0 0 -1 40 95)"
          fill="#E3E6EB"
        />
        <rect
          width="64"
          height="2"
          transform="matrix(1 0 0 -1 40 102)"
          fill="#E3E6EB"
        />
      </g>
      <path d="M63 20H151V80H63V20Z" fill="#366EF4" />
      <mask
        id={mask1Id}
        style={{ maskType: 'alpha' }}
        maskUnits="userSpaceOnUse"
        x="63"
        y="30"
        width="88"
        height="50"
      >
        <path d="M63 30H151V80H63V30Z" fill="#0052D9" />
      </mask>
      <g mask={`url(#${mask1Id})`}>
        <path d="M63 30H151V80H63V30Z" fill="#0052D9" />
        <g opacity="0.3" filter={`url(#${filter2Id})`}>
          <path d="M30 62H118V122H30V62Z" fill="#97A3B7" />
        </g>
      </g>
      <rect x="68" y="24" width="2" height="2" fill="white" />
      <rect x="74" y="24" width="2" height="2" fill="white" />
      <rect x="80" y="24" width="66" height="2" fill="white" />
      <rect
        width="21"
        height="2"
        transform="matrix(1 0 0 -1 73 53)"
        fill="white"
      />
      <rect
        width="64"
        height="2"
        transform="matrix(1 0 0 -1 73 60)"
        fill="white"
      />
      <path
        d="M148.207 80.2698H133.295C130.017 80.2698 127.334 82.9531 127.334 86.2306V108.004C127.334 110.783 129.327 111.971 131.761 110.611L139.294 106.413C140.099 105.972 141.402 105.972 142.188 106.413L149.721 110.611C152.155 111.971 154.148 110.783 154.148 108.004V86.2306C154.167 82.9531 151.484 80.2698 148.207 80.2698Z"
        stroke="black"
        strokeWidth="1.17"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M154.167 86.2306V108.004C154.167 110.783 152.174 111.952 149.74 110.611L142.207 106.413C141.402 105.972 140.099 105.972 139.294 106.413L131.761 110.611C129.327 111.952 127.334 110.783 127.334 108.004V86.2306C127.334 82.9531 130.017 80.2698 133.295 80.2698H148.207C151.484 80.2698 154.167 82.9531 154.167 86.2306Z"
        stroke="black"
        strokeWidth="1.17"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M165.667 78.7941V100.567C165.667 103.347 163.674 104.516 161.24 103.174L154.167 99.2257V86.2307C154.167 82.9532 151.484 80.2699 148.207 80.2699H138.834V78.7941C138.834 75.5166 141.517 72.8333 144.795 72.8333H159.707C162.984 72.8333 165.667 75.5166 165.667 78.7941Z"
        stroke="black"
        strokeWidth="1.17"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M165.667 78.7941V100.567C165.667 103.347 163.674 104.516 161.24 103.174L154.167 99.2257V86.2307C154.167 82.9532 151.484 80.2699 148.207 80.2699H138.834V78.7941C138.834 75.5166 141.517 72.8333 144.795 72.8333H159.707C162.984 72.8333 165.667 75.5166 165.667 78.7941Z"
        fill="white"
        stroke="black"
        strokeWidth="1.17"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <defs>
        <filter
          id={filter0Id}
          x="-38"
          y="34"
          width="180"
          height="160"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="25"
            result="effect1_foregroundBlur_12119_4116"
          />
        </filter>
        <filter
          id={filter1Id}
          x="30"
          y="4"
          width="180"
          height="160"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="25"
            result="effect1_foregroundBlur_12119_4116"
          />
        </filter>
        <filter
          id={filter2Id}
          x="24"
          y="56"
          width="100"
          height="72"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="3"
            result="effect1_foregroundBlur_12119_4116"
          />
        </filter>
      </defs>
    </svg>
  );
};

const SKELETON_FORUM_TAB_WIDTHS = [104, 136, 118];
const SKELETON_GUEST_ROWS = [0, 1];

/**
 * 会聊移动端的大会议程是独立视觉模块。
 * 这里按大会页移动端 AgendaSection 的样式/交互口径实现，但保留会聊侧独立组件边界。
 */
export default function ConferenceChatConferenceAgendaSection({
  data,
  title,
  site,
  loading = false,
}: ConferenceChatConferenceAgendaSectionProps) {
  const isZh = site !== 'en';
  const localeClassName = isZh ? styles.zhSection : styles.enSection;
  const authApi = getConferenceChatAuthApiBySite(site);
  const forums = useMemo(() => getForumList(data), [data]);
  const [chatAuth, setChatAuth] = useState<ConferenceChatAuthInfo>();
  const [activeForumID, setActiveForumID] = useState('');
  const [forumTabsScrollState, setForumTabsScrollState] = useState({
    canScrollLeft: false,
    canScrollRight: false,
  });
  const forumTabsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const nextForum = forums[0];
    // 数据刷新后回到第一个论坛，避免沿用旧论坛 ID 导致会聊模块空白。
    setActiveForumID(nextForum ? getForumID(nextForum, 0) : '');
  }, [forums]);

  useEffect(() => {
    let cancelled = false;

    const loadChatAuth = async () => {
      try {
        const payload = await fetchConferenceChatAuthInfo(authApi);
        if (!cancelled && !payload.loginRequired) {
          setChatAuth(payload.data);
        }
      } catch (error) {
        if (!cancelled) {
          setChatAuth(undefined);
        }
      }
    };

    void loadChatAuth();

    return () => {
      cancelled = true;
    };
  }, [authApi]);

  useEffect(() => {
    let frameID = 0;
    let resizeObserver: ResizeObserver | undefined;

    const updateForumTabsScrollState = () => {
      frameID = 0;
      const forumTabs = forumTabsRef.current;
      if (!forumTabs) {
        setForumTabsScrollState({
          canScrollLeft: false,
          canScrollRight: false,
        });
        return;
      }

      const maxScrollLeft = forumTabs.scrollWidth - forumTabs.clientWidth;
      const hasOverflow = maxScrollLeft > 1;
      const scrollLeft = normalizeForumTabsScrollLeft(
        forumTabs.scrollLeft,
        maxScrollLeft
      );

      setForumTabsScrollState({
        canScrollLeft: hasOverflow && scrollLeft > 0,
        canScrollRight: hasOverflow && scrollLeft < maxScrollLeft,
      });
    };

    const scheduleUpdate = () => {
      if (frameID) return;
      frameID = window.requestAnimationFrame(updateForumTabsScrollState);
    };

    const forumTabs = forumTabsRef.current;
    scheduleUpdate();
    window.addEventListener('resize', scheduleUpdate);
    forumTabs?.addEventListener('scroll', scheduleUpdate, { passive: true });

    if (forumTabs && 'ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(scheduleUpdate);
      resizeObserver.observe(forumTabs);
    }

    return () => {
      if (frameID) {
        window.cancelAnimationFrame(frameID);
      }
      window.removeEventListener('resize', scheduleUpdate);
      forumTabs?.removeEventListener('scroll', scheduleUpdate);
      resizeObserver?.disconnect();
    };
  }, [forums]);

  const activeForum =
    forums.find((forum, index) => getForumID(forum, index) === activeForumID) ||
    forums[0];
  const sessions = useMemo(() => getSessionList(activeForum), [activeForum]);
  const emptyAgendaText = isZh
    ? '暂无议程信息'
    : 'No agenda information available';

  const renderEmptyState = (className?: string) => (
    <div className={`${styles.empty} ${className || ''}`.trim()}>
      <div className={styles.emptyContent}>
        <ConferenceAgendaEmptyStateIllustration />
        <p className={styles.emptyText}>{emptyAgendaText}</p>
      </div>
    </div>
  );

  if (loading) {
    return (
      <section className={`${styles.section} ${localeClassName}`}>
        <h3 className={styles.title}>{title}</h3>
        <div className={styles.forumTabsWrap}>
          <div className={styles.skeletonForumTabs}>
            {SKELETON_FORUM_TAB_WIDTHS.map((width, index) => (
              <span
                key={`${width}-${index}`}
                className={`${styles.skeletonBlock} ${styles.skeletonForumTab}`}
                style={{ width: `${width}px` }}
                aria-hidden="true"
              />
            ))}
          </div>
        </div>
        <div className={styles.sessionList}>
          <section className={styles.sessionSection}>
            <div className={styles.sessionHeader}>
              <span
                className={`${styles.skeletonBlock} ${styles.skeletonSessionTitle}`}
                aria-hidden="true"
              />
              <span
                className={`${styles.skeletonBlock} ${styles.skeletonSessionTime}`}
                aria-hidden="true"
              />
            </div>
            <article
              className={`${styles.agendaCard} ${styles.skeletonAgendaCard}`}
            >
              <div className={styles.agendaCardHeader}>
                <span
                  className={`${styles.skeletonBlock} ${styles.skeletonAgendaLine}`}
                  aria-hidden="true"
                />
                <span
                  className={`${styles.skeletonBlock} ${styles.skeletonAgendaLineShort}`}
                  aria-hidden="true"
                />
              </div>
              <div className={styles.guestList}>
                {SKELETON_GUEST_ROWS.map((item) => (
                  <div key={item} className={styles.skeletonGuest}>
                    <div className={styles.guestMain}>
                      <span
                        className={`${styles.skeletonBlock} ${styles.skeletonGuestAvatar}`}
                        aria-hidden="true"
                      />
                      <div className={styles.skeletonGuestText}>
                        <span
                          className={`${styles.skeletonBlock} ${styles.skeletonGuestName}`}
                          aria-hidden="true"
                        />
                        <span
                          className={`${styles.skeletonBlock} ${styles.skeletonGuestTitle}`}
                          aria-hidden="true"
                        />
                        <span
                          className={`${styles.skeletonBlock} ${styles.skeletonGuestTitleShort}`}
                          aria-hidden="true"
                        />
                      </div>
                    </div>
                    <span
                      className={`${styles.skeletonBlock} ${styles.skeletonGuestAction}`}
                      aria-hidden="true"
                    />
                  </div>
                ))}
              </div>
            </article>
          </section>
        </div>
      </section>
    );
  }

  const scrollActiveForumIntoView = (
    forumTabs: HTMLDivElement,
    forumTab: HTMLButtonElement
  ) => {
    const maxScrollLeft = forumTabs.scrollWidth - forumTabs.clientWidth;
    if (maxScrollLeft <= FORUM_TABS_SCROLL_EDGE_TOLERANCE) {
      return;
    }

    const containerRect = forumTabs.getBoundingClientRect();
    const tabRect = forumTab.getBoundingClientRect();
    const tabLeft = tabRect.left - containerRect.left + forumTabs.scrollLeft;
    const tabRight = tabLeft + tabRect.width;
    const contentEdgeGap =
      forumTabs.offsetLeft < 0 ? MOBILE_FORUM_TAB_EDGE_GAP : 0;
    const visibleLeft = forumTabs.scrollLeft + contentEdgeGap;
    const visibleRight =
      forumTabs.scrollLeft + forumTabs.clientWidth - contentEdgeGap;
    let targetScrollLeft: number | undefined;

    if (tabLeft < visibleLeft) {
      targetScrollLeft = tabLeft - contentEdgeGap;
    } else if (tabRight > visibleRight) {
      targetScrollLeft = tabRight - forumTabs.clientWidth + contentEdgeGap;
    }

    if (targetScrollLeft === undefined) {
      return;
    }

    forumTabs.scrollTo({
      left: normalizeForumTabsScrollLeft(targetScrollLeft, maxScrollLeft),
      behavior: 'smooth',
    });
  };

  const handleGuestAction = (
    event: MouseEvent<HTMLButtonElement>,
    guest?: ConferenceAgendaGuestItem
  ) => {
    event.preventDefault();
    event.stopPropagation();

    const contactId = getGuestID(guest);
    const guestDataSource = getGuestDataSource(guest);
    if (
      isConferenceChatGuestSelf(chatAuth, {
        contactId,
        guestDataSource,
      })
    ) {
      return;
    }

    void openConferenceChatEntry({
      authApi,
      site,
      targetSearchParams:
        contactId && guestDataSource
          ? {
              tab: 'messages',
              contactId,
              guestDataSource,
            }
          : undefined,
    });
  };

  const renderGuest = (guest: ConferenceAgendaGuestItem, index: number) => {
    const name = getGuestName(guest, isZh);
    const titleText = getGuestTitle(guest, isZh);
    const avatar = guest.Avatar || guest.avatar;
    const contactId = getGuestID(guest);
    const guestDataSource = getGuestDataSource(guest);
    const showChatEntry =
      isGuestContactAllowed(guest) &&
      !isConferenceChatGuestSelf(chatAuth, {
        contactId,
        guestDataSource,
      });

    return (
      <div
        className={styles.guest}
        key={`${guest.GuestID || guest.guestId || index}`}
      >
        <div className={styles.guestMain}>
          <div className={styles.guestAvatarWrap}>
            {avatar ? (
              <Image
                src={avatar}
                alt={name}
                className={styles.guestAvatar}
                loading="lazy"
              />
            ) : (
              <span className={styles.guestAvatarFallback}>
                {name.slice(0, 1) || '-'}
              </span>
            )}
          </div>
          <div className={styles.guestText}>
            {name ? <span className={styles.guestName}>{name}</span> : null}
            {titleText ? (
              <span className={styles.guestTitle}>{titleText}</span>
            ) : null}
          </div>
        </div>
        {showChatEntry ? (
          <button
            type="button"
            className={styles.guestMobileAction}
            onClick={(event) => handleGuestAction(event, guest)}
            aria-label={isZh ? '和嘉宾聊天' : 'Chat with guest'}
          >
            <GuestActionIcon />
          </button>
        ) : null}
      </div>
    );
  };

  const renderAgendaCard = (agenda: ConferenceAgendaItem, index: number) => {
    const guests = getGuestList(agenda);
    const hasGuests = guests.length > 0;
    const agendaTitle = getAgendaTitle(agenda, isZh);

    return (
      <article className={styles.agendaCard} key={getAgendaID(agenda, index)}>
        <div
          className={`${styles.agendaCardHeader} ${
            hasGuests ? '' : styles.agendaCardHeaderWithoutGuests
          }`.trim()}
        >
          {agendaTitle ? (
            <h4 className={styles.agendaTitle}>{agendaTitle}</h4>
          ) : null}
        </div>
        {hasGuests ? (
          <div className={styles.guestList}>
            {guests.map((guest, guestIndex) => renderGuest(guest, guestIndex))}
          </div>
        ) : null}
      </article>
    );
  };

  return (
    <section className={`${styles.section} ${localeClassName}`}>
      <h3 className={styles.title}>{title}</h3>

      {forums.length ? (
        <>
          <div className={styles.forumTabsWrap}>
            <div ref={forumTabsRef} className={styles.forumTabs}>
              {forums.map((forum, index) => {
                const forumID = getForumID(forum, index);
                const isActive = forumID === activeForumID;
                const forumTitle = getLocaleText(
                  isZh,
                  forum.TitleCN || forum.titleCn,
                  forum.TitleEN || forum.titleEn
                );
                const subtitle = getLocaleText(
                  isZh,
                  forum.SubtitleCN || forum.subtitleCn,
                  forum.SubtitleEN || forum.subtitleEn
                );

                return (
                  <button
                    key={forumID}
                    type="button"
                    className={`${styles.forumTab} ${
                      isActive ? styles.forumTabActive : ''
                    }`}
                    onClick={(event) => {
                      if (forumTabsRef.current) {
                        scrollActiveForumIntoView(
                          forumTabsRef.current,
                          event.currentTarget
                        );
                      }
                      setActiveForumID(forumID);
                    }}
                  >
                    {forumTitle ? (
                      <span className={styles.forumTabTitle}>{forumTitle}</span>
                    ) : null}
                    {subtitle ? (
                      <span className={styles.forumTabSubtitle}>
                        {subtitle}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
            {forumTabsScrollState.canScrollLeft ? (
              <span
                className={`${styles.forumTabsFade} ${styles.forumTabsFadeLeft}`}
                aria-hidden="true"
              />
            ) : null}
            {forumTabsScrollState.canScrollRight ? (
              <span
                className={`${styles.forumTabsFade} ${styles.forumTabsFadeRight}`}
                aria-hidden="true"
              />
            ) : null}
          </div>

          <div className={styles.sessionList}>
            {sessions.length
              ? sessions.map((session, sessionIndex) => {
                  const agendaList = getAgendaList(session);
                  const sessionTime = formatConferenceAgendaClockRange(
                    session.StartTime ?? session.startTime,
                    session.EndTime ?? session.endTime
                  );
                  const sessionTitle = getLocaleText(
                    isZh,
                    session.TitleCN || session.titleCn,
                    session.TitleEN || session.titleEn
                  );

                  return (
                    <section
                      className={styles.sessionSection}
                      key={getSessionID(session, sessionIndex)}
                    >
                      <div className={styles.sessionHeader}>
                        {sessionTitle ? <strong>{sessionTitle}</strong> : null}
                        {sessionTime ? (
                          <span className={styles.sessionTime}>
                            {sessionTime}
                          </span>
                        ) : null}
                      </div>
                      <div className={styles.agendaList}>
                        {agendaList.length
                          ? agendaList.map((agenda, agendaIndex) =>
                              renderAgendaCard(agenda, agendaIndex)
                            )
                          : renderEmptyState()}
                      </div>
                    </section>
                  );
                })
              : renderEmptyState()}
          </div>
        </>
      ) : (
        renderEmptyState(styles.emptySection)
      )}
    </section>
  );
}
